import {
  createEnumParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'

import { NexusVehicle } from '@nxcr-org/web-api-interface/lib/fleet_service_pb'

import {
  State as VehicleState,
  Vehicle,
} from '@nxcr-org/web-api-interface/lib/vehicle_pb'
import {useContext, useState} from 'react'
import { find, keys, map, toLower } from 'lodash'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FleetService } from '../../api/fleet-service'
import { formattedNexusVehicleStatuses } from '../../api/reservation.formatted-enums'
import { State } from '@nxcr-org/web-api-interface/lib/vehicle_pb'
import Bluebird from 'bluebird'
import { NexusService } from '../../../global-APIs/nexus-service'
import { formatDealerAddress } from '../../../../utils/formatDealerAddress'
import { VehicleData } from '../../../../utils/fileParser'
import { ClientContext } from '../../../../utils/clientContext'
import {GridPaginationModel} from '@mui/x-data-grid'

// Import VehicleFormData type
export interface VehicleFormData {
  programId: string
  financier: string
  vin: string
  make: string
  model: string
  trim: string
  year: number
  exteriorColor: string
  interiorColor: string
  startingOdometerMiles: number
  additionalDetails: string
}

export interface UploadMultipleVehiclesFormData {
  programId: string
  financier: string
  vehicles: VehicleData[]
}

// Custom hook to fetch available vehicle makes
export function useAvailableMakes() {
  const { data: availableMakes } = useQuery({
    queryKey: ['available-makes'],
    queryFn: NexusService.listVehicleMakes,
    placeholderData: ['Tesla', 'Polestar'], // fallback to original values
  })

  return availableMakes || ['Tesla', 'Polestar']
}

export function useVehiclesTable() {
  const availableMakes = useAvailableMakes()
  const queryClient = useQueryClient()
  const { clientId, clients } = useContext(ClientContext)
  const getDefaultMake = (): string => {
    if (!clientId) {
      return 'Tesla'
    }
    const client = clients?.find(c => c.id === clientId)
    if (!client) {
      return 'Tesla'
    }
    if (client.name?.toLowerCase().includes('galpin')) {
      return 'Polestar'
    }
    return 'Tesla'
  }

  const [query, setQuery] = useQueryParams({
    make: withDefault(StringParam, getDefaultMake()),
    status: withDefault(
      createEnumParam(map(keys(NexusVehicle.Status), toLower)),
      'eligible'
    ),
    page: withDefault(NumberParam, 0),
    rowsPerPage: withDefault(NumberParam, 100),
  })

  const [vehicleToList, setVehicleToList] = useState<Vehicle.AsObject>(
    {} as Vehicle.AsObject
  )
  const [fetchListVehiclesError, setFetchListVehiclesError] = useState(false)
  const [isAddVehicleDrawerOpen, setIsAddVehicleDrawerOpen] = useState(false)
  const [isUploadMultipleDrawerOpen, setIsUploadMultipleDrawerOpen] = useState(false)

  const addVehicleMutation = useMutation({
    mutationFn: (data: VehicleFormData) => {
      return NexusService.addVehicle({
        programId: data.programId,
        financier: data.financier,
        vehicleJson: {
          vin: data.vin,
          make: data.make,
          model: data.model,
          trim: data.trim,
          year: data.year,
          exteriorColor: data.exteriorColor,
          interiorColor: data.interiorColor,
          startingOdometerMiles: data.startingOdometerMiles,
          additionalDetails: data.additionalDetails,
        },
      })
    },
    onSuccess: () => {
      setIsAddVehicleDrawerOpen(false)
      // Invalidate and refetch the vehicles query to show the new vehicle
      queryClient.invalidateQueries({queryKey: ['getVehicles']})
    }
  }
  )

  const addMultipleVehiclesMutation = useMutation({
    mutationFn: (data: UploadMultipleVehiclesFormData) => {
      return NexusService.addVehicles({
        programId: data.programId,
        financier: data.financier,
        vehicleJsons: data.vehicles.map(vehicle => ({
          vin: vehicle.vin,
          make: vehicle.make,
          model: vehicle.model,
          trim: vehicle.trim,
          year: vehicle.year,
          exteriorColor: vehicle.exteriorColor,
          interiorColor: vehicle.interiorColor,
          startingOdometerMiles: vehicle.startingOdometerMiles,
          additionalDetails: vehicle.additionalDetails,
        })),
      })
    },
      onSuccess: () => {
        setIsUploadMultipleDrawerOpen(false)
        // Invalidate and refetch the vehicles query to show the new vehicles
        queryClient.invalidateQueries({ queryKey: ['getVehicles']})
      }
    }
  )

  const handleAddVehicle = async (data: VehicleFormData) => {
    addVehicleMutation.mutate(data)
  }

  const handleUploadMultipleVehicles = async (data: UploadMultipleVehiclesFormData) => {
    addMultipleVehiclesMutation.mutate(data)
  }

  const selectTab = (selectedTab: string) => {
    setQuery({ status: selectedTab.toLowerCase() as Lowercase<string> })
  }

  /**
   * Toggles a vehicle between IN_LISTING and IN_PROCESS states.
   * Throws an error when the vehicle is not in one of these states.
   *
   * @returns {Promise} - A promise that resolves to the updated vehicle
   */
  const listDelistMutation = useMutation({
  mutationFn: () =>
  {
    // Toggle between IN_LISTING and IN_PROCESS states
    if (vehicleToList.state === State.IN_LISTING) {
      return FleetService.updateStateToInProcess(vehicleToList.id)
    }

    if (vehicleToList.state === State.IN_PROCESS) {
      return FleetService.updateStateToInListing(vehicleToList.id)
    }
    throw new Error('Invalid state')
  }
}
  )

  const listVehicle = (vehicle: Vehicle.AsObject) => {
    setVehicleToList(vehicle)
  }

  const onListVehicleClose = () => {
    setVehicleToList({} as Vehicle.AsObject)
  }

  const setPaginationModel = (paginationModel: GridPaginationModel) => {
    setQuery({ page: paginationModel.page, rowsPerPage: paginationModel.pageSize })
  }

  const vehicleQuery = useQuery({
    queryKey: ['getVehicles', query],
    queryFn: async () => {
      try {
        const res = await FleetService.getVehicles({
          criteriaList: [
            {
              property: 'status',
              query: query.status
            },
            {
              property: 'make',
              query: query.make
            },
          ],
          listByStatus: false,
          trimPropertiesList: [],
          page: query.page,
          resultsPerPage: query.rowsPerPage,
          customerId: ''
        }, null)
        return {
          total: res.total,
          vehiclesList: await createVehicleRows(res.vehiclesList)
        }
      } catch (error) {
        setFetchListVehiclesError(true)
        console.log('error', error)
        return {
          total: 0,
          vehiclesList: []
        }
      }
    },
    placeholderData: (previousData) => previousData,
    }
  )

  return {
    total: vehicleQuery?.data?.total,
    vehicles: vehicleQuery?.data?.vehiclesList,
    vehicleQuery,
    activeTab: query.status,
    selectTab,
    listVehicle,
    vehicleToList,
    onListVehicleClose,
    tabs: formattedNexusVehicleStatuses,
    query,
    setQuery,
    fetchListVehiclesError,
    listDelistMutation,
    availableMakes,
    addVehicleMutation,
    handleAddVehicle,
    isAddVehicleDrawerOpen,
    setIsAddVehicleDrawerOpen,
    addMultipleVehiclesMutation,
    handleUploadMultipleVehicles,
    isUploadMultipleDrawerOpen,
    setIsUploadMultipleDrawerOpen,
    setPaginationModel,
  }
}

export async function createVehicleRows(vehicles: NexusVehicle.AsObject[]) {
  return Bluebird.map(vehicles, async ({ vehicle, status }) => {
    const latestCustody = await NexusService.getLatestCustody({
      vehicleId: vehicle.id,
    })
    const telematics = await FleetService.getTelematics(vehicle.id)

    const isEligible = vehicle.state === VehicleState.IN_LISTING

    return {
      isEligible,
      id: vehicle.id,
      make: vehicle.make,
      model: vehicle.model,
      trim: vehicle.trim,
      vin: vehicle.vin,
      color: vehicle?.exteriorColorsList[0]?.title || 'N/A',
      miles: telematics?.telematicsList?.[0]?.odometer?.reading?.toString() || 'N/A',
      status: getVehicleStatus(status),
      dealerAddress: formatDealerAddress(latestCustody?.dealership?.address),
      dealerName: latestCustody?.dealership?.name,
      bodyType: vehicle.profilesList?.[0]?.mediaListList?.[1]?.data,
      dateUpdated:
        latestCustody?.dealership?.updatedTime &&
        new Date(latestCustody?.dealership?.updatedTime).toLocaleDateString(),
      list: {
        vehicle,
        isEligible,
      },
    }
  })
}

export function getVehicleStatus(status: NexusVehicle.AsObject['status']) {
  const state = find(formattedNexusVehicleStatuses, (s) => {
    return s.value === status
  })

  if (!state) {
    return 'Unknown Status'
  }

  return state.label
}

